import {
  SectionCard, Skeleton, SkeletonAvatar, SkeletonButton, SkeletonInput, SkeletonRegion,
} from '@parsina/framework';
import { C, RADIUS } from '@parsina/framework/tokens';
import { PANEL_PAD, STACK } from '../../constants/density';
import { useTranslation } from '../../i18n';
import useResponsive from '../../hooks/useResponsive';
import { MobileScreen, MobileHeader } from '../../mobile';
import { ClipboardIcon, ShieldIcon, UserIcon } from './icons';

/**
 * «ثبت بیمار جدید» while it loads — the registration form's own shape.
 *
 * The router's Suspense fallback for `/patients/new`, so it is imported
 * eagerly and stays light: kit primitives, tokens, density, i18n, the mobile
 * shell and the section icons — never the form hook, the voice module or the
 * insurer query. The three numbered `SectionCard`s render for REAL (their
 * titles are static) with each card's field grid in placeholder blocks, so the
 * form that replaces this one lands in the same boxes.
 *
 * `InsurerSelectorSkeleton` is the one region the LOADED screen still waits
 * on: بیمه is required and its catalog is a query, so the screen draws the
 * selector's tiles as blocks until the insurers answer, in place of a spinner
 * beside a sentence (DESIGN.md §"Loading & skeleton" — region load).
 */

const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 };

/**
 * The `CardSelector` tiles: `columns` per row, at the kit tile's height (icon,
 * label, `py-3.5`), with the required-field label above.
 */
export function InsurerSelectorSkeleton({
  columns = 4, count = columns, label,
  /** False inside a page skeleton that already announces itself. */
  standalone = true,
  ...rest
}) {
  const { t } = useTranslation();
  return (
    <div
      {...(standalone
        ? { role: 'status', 'aria-busy': 'true', 'aria-label': label || t('patients.mobile.insurersLoading'), 'data-testid': 'insurers-skeleton' }
        : { 'aria-hidden': true })}
      data-state="loading"
      style={{ display: 'grid', gap: 6 }}
      {...rest}
    >
      <Skeleton width={72} height={12} />
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 8 }}>
        {Array.from({ length: count }, (_, i) => (
          <Skeleton key={i} height={74} radius={12} style={{ width: '100%' }} />
        ))}
      </div>
    </div>
  );
}

/** The «اطلاعات پایه» card body: the name, then three two-up rows, then the two toggle groups. */
function BasicsFields({ mobile }) {
  const row = mobile ? undefined : grid2;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <SkeletonInput />
      <div style={row}>
        <SkeletonInput />
        <SkeletonInput />
      </div>
      <div style={row}>
        <SkeletonInput />
        <SkeletonInput />
      </div>
      <div style={row}>
        <SkeletonInput />
        <SkeletonInput />
      </div>
    </div>
  );
}

/** The «بیمه» card body: the selector tiles and the policy number. */
function InsuranceFields({ mobile }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <InsurerSelectorSkeleton columns={mobile ? 2 : 4} count={4} standalone={false} />
      <SkeletonInput />
    </div>
  );
}

/** The «سوابق» card body: the drug search, the allergy chips, the notes textarea. */
function HistoryFields() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SkeletonInput />
      <div style={{ display: 'grid', gap: 6 }}>
        <Skeleton width={88} height={12} />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} width={64 + (i % 2) * 20} height={30} radius={RADIUS.pill} />
          ))}
        </div>
      </div>
      <SkeletonInput multiline />
    </div>
  );
}

// ── Desktop ────────────────────────────────────────────────────────────────────

const card = {
  background: C.card, border: `1px solid ${C.border}`, borderRadius: 18, padding: '18px 20px',
};

export function AddPatientSkeletonWeb() {
  const { t } = useTranslation();
  return (
    <SkeletonRegion
      label={t('common.state.loading')}
      data-testid="add-patient-skeleton"
      style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: C.bg }}>
      <header style={{
        flexShrink: 0, background: C.card, borderBottom: `1px solid ${C.border}`, padding: PANEL_PAD.regular,
      }}>
        <div style={{ fontSize: '0.71875rem', color: C.textMuted, marginBottom: 5 }}>
          {t('common.brand.name')}
          <span style={{ margin: '0 5px' }}>/</span>
          {t('nav.items.patients')}
          <span style={{ margin: '0 5px' }}>/</span>
          <b style={{ color: C.textSecondary }}>{t('patients.actions.addNew')}</b>
        </div>
        <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: C.textPrimary }}>{t('patients.actions.addNew')}</h1>
        <p style={{ margin: '3px 0 0', fontSize: '0.78125rem', color: C.textMuted }}>{t('patients.form.subtitle')}</p>
      </header>

      <main style={{ overflowY: 'auto', flex: 1 }}>
        <div style={{ display: 'flex', gap: 20, padding: '18px 24px 48px', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* The inline voice bar's slot. */}
            <Skeleton height={56} radius={14} />

            <SectionCard n={1} title={t('patients.sections.basics')} sub={t('patients.sections.basicsHint')} icon={UserIcon}>
              <BasicsFields />
            </SectionCard>
            <SectionCard n={2} title={t('patients.sections.insurance')} sub={t('patients.sections.insuranceHint')} icon={ShieldIcon}>
              <InsuranceFields />
            </SectionCard>
            <SectionCard n={3} title={t('patients.sections.history')} sub={t('patients.sections.historyHint')} icon={ClipboardIcon}>
              <HistoryFields />
            </SectionCard>
          </div>

          <aside style={{ width: 290, flexShrink: 0, position: 'sticky', top: 18, display: 'flex', flexDirection: 'column', gap: STACK.regular }}>
            <div style={card}>
              <div style={{ fontSize: '0.78125rem', fontWeight: 700, color: C.textMuted, marginBottom: 14, textAlign: 'center' }}>
                {t('patients.preview.title')}
              </div>
              <div style={{ display: 'grid', placeItems: 'center', marginBottom: 10 }}>
                <SkeletonAvatar size={60} />
              </div>
              <Skeleton width={140} height={18} style={{ margin: '0 auto 14px' }} />
              {Array.from({ length: 8 }, (_, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${C.border}` }}>
                  <Skeleton width={70} height={12} />
                  <Skeleton width={56} height={12} />
                </div>
              ))}
            </div>

            <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: STACK.tight }}>
              {Array.from({ length: 4 }, (_, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Skeleton width={18} circle />
                  <Skeleton width={110 + (i % 2) * 30} height={12} />
                </div>
              ))}
            </div>

            <SkeletonButton width="100%" style={{ height: 48 }} />
            <SkeletonButton width="100%" />
          </aside>
        </div>
      </main>
    </SkeletonRegion>
  );
}

// ── Mobile ─────────────────────────────────────────────────────────────────────

export function AddPatientSkeletonMobile() {
  const { t } = useTranslation();
  return (
    <MobileScreen
      header={(
        <MobileHeader
          title={t('patients.actions.addNew')}
          subtitle={t('patients.form.subtitle')}
        />
      )}
      cta={(
        <div aria-hidden style={{ display: 'grid', gap: '0.5rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {Array.from({ length: 3 }, (_, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3125rem' }}>
                <Skeleton width={14} circle />
                <Skeleton width={56 + (i % 2) * 16} height={11} />
              </span>
            ))}
          </div>
          <SkeletonButton width="100%" />
          <SkeletonButton width="100%" />
        </div>
      )}
      data-state="loading"
      data-testid="add-patient-skeleton"
    >
      <SkeletonRegion
        label={t('common.state.loading')}
        style={{ padding: '0.75rem 0.75rem 0', display: 'grid', gap: `${STACK.regular}px` }}>
        <Skeleton height={56} radius={14} />
        <SectionCard n={1} title={t('patients.sections.basics')} icon={UserIcon}>
          <BasicsFields mobile />
        </SectionCard>
        <SectionCard n={2} title={t('patients.sections.insurance')} icon={ShieldIcon}>
          <InsuranceFields mobile />
        </SectionCard>
        <SectionCard n={3} title={t('patients.sections.history')} icon={ClipboardIcon}>
          <HistoryFields />
        </SectionCard>
      </SkeletonRegion>
    </MobileScreen>
  );
}

export default function AddPatientSkeleton(props) {
  const { isMobile } = useResponsive();
  return isMobile ? <AddPatientSkeletonMobile {...props} /> : <AddPatientSkeletonWeb {...props} />;
}
